/**
 * Разбивает переданный ArrayBuffer на части фиксированного размера.
 *
 * @param buffer - Исходный буфер, который необходимо разбить.
 * @param chunkSize - Размер каждой части в байтах.
 * @returns Массив ArrayBuffer, каждый из которых представляет собой часть исходного буфера.
 *
 * @example
 * const buffer = new ArrayBuffer(10);
 * const chunks = splitBuffer(buffer, 4);
 * console.log(chunks.length); // 3
 */
export const splitBuffer = (buffer: ArrayBuffer, chunkSize: number): ArrayBuffer[] => {
    const chunks: ArrayBuffer[] = [];
    const totalChunks = Math.ceil(buffer.byteLength / chunkSize);
    const uint8Array = new Uint8Array(buffer);

    for (let i = 0; i < totalChunks; i += 1) {
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, buffer.byteLength);
        chunks.push(uint8Array.slice(start, end).buffer);
    }

    return chunks;
};

/**
 * Объединяет массив ArrayBuffer в один единый буфер.
 *
 * @param buffers - Массив ArrayBuffer, которые необходимо объединить.
 * @returns Новый ArrayBuffer, содержащий данные из всех переданных буферов.
 *
 * @example
 * const buffer1 = new ArrayBuffer(4);
 * const buffer2 = new ArrayBuffer(6);
 * const result = concatenateBuffers([buffer1, buffer2]);
 * console.log(result.byteLength); // 10
 */
export const concatenateBuffers = (buffers: ArrayBuffer[]): ArrayBuffer => {
    const totalLength = buffers.reduce((sum, buffer) => sum + buffer.byteLength, 0);
    const result = new Uint8Array(totalLength);

    let offset = 0;
    buffers.forEach(buffer => {
        result.set(new Uint8Array(buffer), offset);
        offset += buffer.byteLength;
    });

    return result.buffer;
};

/**
 * Конвертирует строку хэша в ArrayBuffer, где каждый символ строки
 * интерпретируется как 8-битное значение.
 *
 * @param hash - Строка, представляющая хэш.
 * @returns ArrayBuffer, содержащий байты, соответствующие символам строки.
 *
 * @example
 * const hash = "abc123";
 * const buffer = stringToArrayBuffer(hash);
 * console.log(buffer.byteLength); // 6
 */
export const stringToArrayBuffer = (hash: string): ArrayBuffer => {
    const uint8Array = new Uint8Array(hash.length);
    for (let i = 0; i < hash.length; i += 1) {
        uint8Array[i] = hash.charCodeAt(i);
    }
    return uint8Array.buffer;
};
